/**
 * ReplyCard — shared outreach reply component (Sprint 7.6 Bloque A).
 * Dumb / presentational. Hooks live in module-specific wrappers.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { MessageComposer } from '../ui/MessageComposer.js';
import { timeAgo, htmlToText } from './utils.js';
import type { NormalizedReply, ReplyCardLabels, MutationState } from './types.js';

interface ReplyCardProps {
  reply: NormalizedReply;
  labels: ReplyCardLabels;
  onVerify?: () => void;   // undefined = no aplica (Projects)
  onDismiss?: () => void;  // undefined = no aplica (Projects)
  onReply?: (body: { bodyHtml: string; bodyText: string; attachments?: File[] }) => void;
  replyState?: MutationState;
  allowAttachments?: boolean;
}

export function ReplyCard({
  reply,
  labels,
  onVerify,
  onDismiss,
  onReply,
  replyState,
  allowAttachments = false,
}: ReplyCardProps) {
  const { i18n } = useTranslation();
  const [showQuote, setShowQuote] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerBody, setComposerBody] = useState('');

  const isOutbound = reply.direction === 'outbound';
  const isVerified = reply.isVerified === 1;
  const borderColor = isOutbound ? 'var(--evo-stroke)' : (isVerified ? 'var(--evo-cyan)' : 'var(--evo-amber)');
  const displayFrom = reply.fromAddress || (reply.fromDomain ? `@${reply.fromDomain}` : '—');
  const dateMs = reply.timestamp ? (typeof reply.timestamp === 'number' ? reply.timestamp : Number(reply.timestamp)) : null;
  const dateStr = dateMs
    ? new Date(dateMs).toLocaleString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : `${timeAgo(reply.receivedAt)} ago`;

  const bodyText = reply.replyText ?? reply.bodyText ?? null;
  const replyPending = replyState?.isPending ?? false;

  function handleSend(attachments?: File[]) {
    const text = htmlToText(composerBody);
    if (!text.trim()) return;
    onReply?.({
      bodyHtml: composerBody,
      bodyText: text,
      ...(allowAttachments ? { attachments: attachments ?? [] } : {}),
    });
    setComposerOpen(false);
    setComposerBody('');
  }

  return (
    <div style={{ border: `1px solid ${borderColor}`, borderRadius: 'var(--evo-r-md)', overflow: 'hidden' }}>
      {/* Banner */}
      {isOutbound ? (
        <div style={{ padding: '8px 16px', background: 'rgba(100,100,100,0.04)', borderBottom: '1px solid var(--evo-stroke)' }}>
          <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', fontWeight: 'var(--evo-fw-semibold)', letterSpacing: '0.5px' }}>
            {labels.outbound}
          </span>
        </div>
      ) : (
        <>
          {!isVerified && onVerify && onDismiss && (
            <div style={{ padding: '8px 16px', background: 'rgba(251,191,36,0.08)', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-amber)', fontWeight: 'var(--evo-fw-semibold)' }}>
                {labels.possible}
              </span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Button variant="outline" tone="cyan" size="sm" onClick={onVerify}>{labels.confirm}</Button>
                <Button variant="ghost" tone="muted" size="sm" onClick={onDismiss}>{labels.dismiss}</Button>
              </div>
            </div>
          )}
          {(isVerified || (!onVerify && !onDismiss)) && (
            <div style={{ padding: '8px 16px', background: 'rgba(0,220,130,0.06)', borderBottom: `1px solid ${borderColor}` }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)', fontWeight: 'var(--evo-fw-semibold)' }}>
                {(!onVerify && !onDismiss) ? `${labels.inbound} — ${timeAgo(reply.receivedAt)} ago` : labels.verified}
              </span>
            </div>
          )}
        </>
      )}

      {/* Meta */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {isOutbound ? (
          <>
            {reply.toAddress && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.to}</span>
                <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reply.toAddress}</span>
              </div>
            )}
            {reply.subject && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.subject}</span>
                <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reply.subject}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.sent}</span>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>{dateStr}</span>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.from}</span>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayFrom}</span>
            </div>
            {reply.subject && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.subject}</span>
                <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reply.subject}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.received}</span>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>{dateStr}</span>
            </div>
          </>
        )}
      </div>

      {/* Reply body */}
      {bodyText && (
        <>
          <div style={{ height: '1px', background: 'var(--evo-stroke)', margin: '0 16px' }} />
          <div style={{ padding: '12px 16px' }}>
            <p style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {reply.replyText ? bodyText : bodyText.slice(0, 800)}
            </p>
            {reply.quoteText && (
              <div style={{ marginTop: '8px' }}>
                <Button variant="link" tone="muted" size="sm" onClick={() => setShowQuote(s => !s)}>
                  {showQuote ? labels.hideQuote : labels.showQuote} {showQuote ? '▴' : '▾'}
                </Button>
                {showQuote && (
                  <div style={{ marginTop: '6px', paddingLeft: '10px', borderLeft: '2px solid var(--evo-stroke)' }}>
                    <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {reply.quoteText}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Actions / Composer */}
      {onReply && (
        <div style={{ borderTop: '1px solid var(--evo-stroke)', padding: '10px 16px' }}>
          {!composerOpen ? (
            <Button variant="outline" tone="cyan" size="sm" onClick={() => setComposerOpen(true)}>{labels.replyBtn}</Button>
          ) : (
            <MessageComposer
              value={composerBody}
              onChange={setComposerBody}
              placeholder={labels.composerPlaceholder}
              primaryLabel={labels.sendReplyBtn}
              loadingLabel={labels.sending}
              onPrimary={handleSend}
              onCancel={() => { setComposerOpen(false); setComposerBody(''); replyState?.reset?.(); }}
              loading={replyPending}
              allowAttachments={allowAttachments}
              attachLabel={labels.attachBtn}
              sizeExceededLabel={labels.sizeExceeded}
              error={replyState?.isError ? (replyState.error?.message ?? labels.actionFailed) : null}
              contextLabel={labels.replyTo.replace('{to}', displayFrom)}
            />
          )}
        </div>
      )}
    </div>
  );
}
